/**
 * La sonnerie d'une alerte urgente, synthétisée plutôt que jouée depuis un fichier.
 *
 * Trois raisons, dans l'ordre où elles comptent :
 *
 * 1. **Elle doit sonner hors ligne.** Un fichier à télécharger ne sonne pas quand le
 *    réseau vacille — exactement la situation où l'on alerte.
 * 2. **Le volume est le nôtre.** Un `<audio>` est plafonné par le niveau enregistré dans
 *    le fichier ; un gain synthétisé part à pleine échelle quel que soit l'appareil.
 * 3. Elle ne pèse rien et ne se met pas en cache de travers.
 *
 * Le motif est **deux notes alternées**, pas une sirène continue : une note tenue se
 * confond avec un acouphène ou une alarme d'immeuble, et le cerveau la range en bruit de
 * fond en quelques secondes. Une alternance reste une information.
 */

const NOTE_A = 880;
const NOTE_B = 1174;
const DUREE_NOTE = 0.28;
const SILENCE = 0.12;

export interface Sonnerie {
  arreter: () => void;
}

/**
 * Fait sonner jusqu'à `arreter()`.
 *
 * Rend `null` si le navigateur n'a pas d'audio, ou si le contexte reste suspendu : sans
 * geste préalable de l'utilisateur sur la page, un navigateur refuse de produire du son —
 * une règle qu'on ne contourne pas et qu'il faut donc savoir signaler à l'écran plutôt que
 * de laisser croire que ça sonne.
 */
export function sonner(): Sonnerie | null {
  const Contexte =
    globalThis.AudioContext ??
    (globalThis as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Contexte) return null;

  let contexte: AudioContext;
  try {
    contexte = new Contexte();
  } catch {
    return null;
  }

  void contexte.resume?.();

  const gain = contexte.createGain();
  // Plein volume, comme demandé. La rampe de 20 ms n'adoucit pas le niveau : elle évite
  // le claquement d'un signal qui démarre à pic, lequel s'entend comme un défaut et non
  // comme une alerte.
  gain.gain.setValueAtTime(0, contexte.currentTime);
  gain.gain.linearRampToValueAtTime(1, contexte.currentTime + 0.02);
  gain.connect(contexte.destination);

  let vivant = true;
  let oscillateur: OscillatorNode | null = null;

  const jouer = (rang: number) => {
    if (!vivant) return;
    const debut = contexte.currentTime;
    oscillateur = contexte.createOscillator();
    oscillateur.type = 'square';
    oscillateur.frequency.setValueAtTime(rang % 2 === 0 ? NOTE_A : NOTE_B, debut);
    oscillateur.connect(gain);
    oscillateur.start(debut);
    oscillateur.stop(debut + DUREE_NOTE);
    oscillateur.onended = () => {
      if (!vivant) return;
      globalThis.setTimeout(() => jouer(rang + 1), SILENCE * 1000);
    };
  };

  jouer(0);

  return {
    arreter: () => {
      vivant = false;
      try {
        oscillateur?.stop();
      } catch {
        /* déjà arrêté */
      }
      void contexte.close?.();
    },
  };
}

/**
 * Le navigateur laisse-t-il produire du son sans geste préalable ?
 *
 * Interrogé avant d'annoncer une sonnerie : promettre un son qui ne partira pas est pire
 * que de dire qu'il faut toucher l'écran.
 */
export function sonAutorise(): boolean {
  const Contexte =
    globalThis.AudioContext ??
    (globalThis as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Contexte) return false;
  try {
    const sonde = new Contexte();
    const etat = sonde.state;
    void sonde.close?.();
    return etat === 'running';
  } catch {
    return false;
  }
}
